import { findOnlineDriversLocationApi } from "api/dispatch";
import { fetchTripStatisticsApi } from "api/trips";
import { useNotificationContext } from "contexts/NotificationContext";
import { IDriverLocation, ILocation } from "models/dispatch";
import { ITripStatistics } from "models/trips";
import { useEffect, useState, useMemo, useRef } from "react";

export default function useDispatchDashboard() {
  const { showError } = useNotificationContext();
  const [showDriverInfo, setShowDriverInfo] = useState<{
    [key: string]: boolean;
  }>({});
  const [driverLocation, setDriverLocation] = useState<IDriverLocation[]>([]);
  const [currentLocation, setCurrentLocation] = useState<ILocation>({
    lat: 13.496494229233356,
    lng: 39.47494624044569,
  });
  const [tripOverview, setTripOverview] = useState<ITripStatistics>();
  const [isRefreshingDrivers, setIsRefreshingDrivers] = useState(false);
  const [recentlyResetDrivers, setRecentlyResetDrivers] = useState<Set<string>>(new Set());
  const [excludeStuckDrivers, setExcludeStuckDrivers] = useState(false);
  const intervalRef = useRef<NodeJS.Timer>();

  useEffect(() => {
    fetchDriversLocation();
    fetchTripStatistics();
    intervalRef.current = setInterval(fetchDriversLocation, 4000);

    // Listen for refresh events from other components (like StuckDrivers)
    const handleRefreshEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("📡 Received refreshDriverLocations event:", {
        resetDriverId: customEvent.detail?.resetDriverId,
        current_recently_reset: Array.from(recentlyResetDrivers)
      });

      if (customEvent.detail?.resetDriverId) {
        // Mark this driver as recently reset
        setRecentlyResetDrivers(prev => {
          const newSet = new Set([...Array.from(prev), customEvent.detail.resetDriverId]);
          console.log("✅ Added driver to recentlyResetDrivers:", {
            driverId: customEvent.detail.resetDriverId,
            new_set: Array.from(newSet)
          });
          return newSet;
        });

        // Clear the recently reset flag after 2 minutes
        setTimeout(() => {
          setRecentlyResetDrivers(prev => {
            const newSet = new Set(Array.from(prev));
            newSet.delete(customEvent.detail?.resetDriverId);
            console.log("⏰ Cleared recently reset driver after 2 minutes:", {
              driverId: customEvent.detail?.resetDriverId,
              remaining: Array.from(newSet)
            });
            return newSet;
          });
        }, 2 * 60 * 1000); // 2 minutes
      }
      fetchDriversLocation();
    };

    window.addEventListener('refreshDriverLocations', handleRefreshEvent);

    // Removed navigator.geolocation to enforce Mekelle focus by default
    // eslint-disable-next-line

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('refreshDriverLocations', handleRefreshEvent);
    };
  }, []); // eslint-disable-line

  const fetchTripStatistics = async () => {
    const { data, error } = await fetchTripStatisticsApi();
    if (data)
      setTripOverview({
        ...data.data,
        total:
          data.data.available +
          data.data.cancelled +
          data.data.completed +
          data.data.pending +
          data.data.ongoing,
      });
    else showError(error?.message ?? "");
  };

  const fetchDriversLocation = async () => {
    // Add console.log when fetching/updating driver locations:
    console.log('Fetching driver locations...', new Date());

    const { data, error } = await findOnlineDriversLocationApi({
      ...currentLocation,
      exclude_stuck: excludeStuckDrivers
    });
    if (data && data.data) {
      // Filter to only show drivers who are online
      let onlineDrivers = data.data;

      // If is_online field exists in response, filter by it
      if (data.data.length > 0 && 'is_online' in data.data[0]) {
        onlineDrivers = data.data.filter((driver: IDriverLocation) => driver.is_online === true);
      }

      // For dashboard, we want to show drivers but mark stuck ones differently
      // Don't filter out drivers with old updates - instead mark them as potentially stuck
      let driversWithStatus = onlineDrivers.map((driver: IDriverLocation) => {
        let stuckLevel = 'active'; // 'active', 'caution', 'warning', 'critical'
        let lastUpdateMinutes = 0;

        if (driver.last_updated && !recentlyResetDrivers.has(driver.id)) {
          const lastUpdated = new Date(driver.last_updated);
          const now = Date.now();
          lastUpdateMinutes = (now - lastUpdated.getTime()) / (1000 * 60);

          // Graduated stuck levels
          if (lastUpdateMinutes > 24 * 60) { // More than 24 hours
            stuckLevel = 'critical';
          } else if (lastUpdateMinutes > 60) { // More than 1 hour
            stuckLevel = 'warning';
          } else if (lastUpdateMinutes > 5) { // More than 5 minutes
            stuckLevel = 'caution';
          }
        }

        // Debug logging for stuck drivers
        if (stuckLevel !== 'active') {
          console.log(`🚨 Driver ${driver.id} status: ${stuckLevel}`, {
            last_updated: driver.last_updated,
            time_diff_minutes: lastUpdateMinutes,
            recently_reset: recentlyResetDrivers.has(driver.id)
          });
        }

        return { ...driver, stuckLevel, lastUpdateMinutes };
      });

      setDriverLocation(driversWithStatus);
      setShowDriverInfo((prev) => {
        const newDrivers: { [key: string]: boolean } = {};
        for (const driver of driversWithStatus) {
          if (!showDriverInfo[driver.id]) newDrivers[driver.id] = false;
        }
        return { ...prev, ...newDrivers };
      });

      console.log('Received driver data:', driversWithStatus);

      // Debug: Log stuck drivers
      const stuckDrivers = driversWithStatus.filter(d => (d as any).isStuck);
      if (stuckDrivers.length > 0) {
        console.log("🚨 Stuck drivers detected:", stuckDrivers.map(d => ({
          id: d.id,
          last_updated: d.last_updated,
          latitude: d.latitude,
          longitude: d.longitude,
          isStuck: (d as any).isStuck
        })));
      }
    } else showError(error?.message ?? "");
  };

  const handleShowDriverInfo = (id: string) =>
    setShowDriverInfo((prev) => {
      const temp = { ...prev };
      temp[id] = true;
      return temp;
    });

  const handleCloseDriverInfo = (id: string) =>
    setShowDriverInfo((prev) => {
      const temp = { ...prev };
      temp[id] = false;
      return temp;
    });

  const refreshDriversLocation = async () => {
    setIsRefreshingDrivers(true);
    try {
      // Force immediate refresh of driver locations
      await fetchDriversLocation();
      console.log("🔄 Driver locations refreshed manually");
    } catch (error) {
      console.error("❌ Failed to refresh driver locations:", error);
    } finally {
      setIsRefreshingDrivers(false);
    }
  };

  const dispatchedByAppPercent = useMemo(() => {
    const dispatchedByApp = tripOverview?.dispatched_by_app ?? 0;
    const dispatchedByDispatcher = tripOverview?.dispatched_by_dispatcher ?? 0;
    const total = dispatchedByApp + dispatchedByDispatcher;
    return (dispatchedByApp * 100) / (total === 0 ? 1 : total);
  }, [tripOverview?.dispatched_by_app, tripOverview?.dispatched_by_dispatcher]);

  const dispatchedByDispatcherPercent = useMemo(() => {
    const dispatchedByApp = tripOverview?.dispatched_by_app ?? 0;
    const dispatchedByDispatcher = tripOverview?.dispatched_by_dispatcher ?? 0;
    const total = dispatchedByApp + dispatchedByDispatcher;
    return (dispatchedByDispatcher * 100) / (total === 0 ? 1 : total);
  }, [tripOverview?.dispatched_by_app, tripOverview?.dispatched_by_dispatcher]);

  return {
    handleCloseDriverInfo,
    handleShowDriverInfo,
    refreshDriversLocation,
    isRefreshingDrivers,
    currentLocation,
    driverLocation,
    showDriverInfo,
    tripOverview,
    dispatchedByAppPercent,
    dispatchedByDispatcherPercent,
    excludeStuckDrivers,
    setExcludeStuckDrivers,
  };
}
